import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { LocationStrategy, HashLocationStrategy, CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { PERFECT_SCROLLBAR_CONFIG } from 'ngx-perfect-scrollbar';
import { PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true
};

import { AppComponent } from './app.component';

// Import containers
import { DefaultLayoutComponent } from './containers';

import { P404Component } from './views/error/404.component';
import { P500Component } from './views/error/500.component';
import { LoginComponent } from './views/login/login.component';

const APP_CONTAINERS = [
  DefaultLayoutComponent
];

import {
  AppAsideModule,
  AppBreadcrumbModule,
  AppHeaderModule,
  AppFooterModule,
  AppSidebarModule,
} from '@coreui/angular';

// Import routing module
import { AppRoutingModule } from './app.routing';

// Import 3rd party components
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { ChartsModule } from 'ng2-charts';
import { TableDefinitionHttpService } from './services/http/common/table-definition.http.service';
import { ColumnDefinitionHttpService } from './services/http/common/column-definition.http.service';
import { ConfigValuesHttpService } from './services/http/common/config-values.service';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthenticationService } from './services/authentication.service';
import { CountyHttpService } from './services/http/administration/county.http.service';
import { TokenInterceptor } from './services/http/common/token-interceptor.service';
import { CountryHttpService } from './services/http/administration/contry.http.service';
import { BsModalRef, BsModalService, ModalModule } from 'ngx-bootstrap/modal';
import { AuthGuard } from './services/auth.guard';
import { CountryListComponent } from './forms/administrations/countries/country.list';
import { CountryManageComponent } from './forms/administrations/countries/country.manage';
import { CountryDetailComponent } from './forms/administrations/countries/country.detail';
import { CountyManageComponent } from './forms/administrations/counties/county.manage';
import { CountyListComponent } from './forms/administrations/counties/county.list';
import { CountyDetailComponent } from './forms/administrations/counties/county.detail';
import { CityListComponent } from './forms/administrations/cities/city.list';
import { CityManageComponent } from './forms/administrations/cities/city.manage';
import { CityDetailComponent } from './forms/administrations/cities/city.detail';
import { AssetManageComponent } from './forms/assets/assets/asset.manage';
import { PaginationComponent, PaginationModule } from 'ngx-bootstrap/pagination';
import { AssetRecoManageComponent } from './forms/assets/assets/asset-reco.manage';
import { AssetRecoListComponent } from './forms/assets/assets/asset-reco.list';
import { DepartmentListComponent } from './forms/administrations/departments/department.list';
import { EmployeeListComponent } from './forms/administrations/employees/employee.list';
import { RegionListComponent } from './forms/administrations/regions/region.list';
import { AdmCenterListComponent } from './forms/administrations/adm-centers/adm-center.list';
import { LocationListComponent } from './forms/administrations/locations/location.list';
import { RoomListComponent } from './forms/administrations/rooms/room.list';
import { AdministrationListComponent } from './forms/administrations/administrations/administration.list';
import { AssetCategoryListComponent } from './forms/assets/asset-categories/asset-category.list';
import { UomListComponent } from './forms/assets/uoms/uom.list';
import { CompanyListComponent } from './forms/assets/companies/company.list';
import { AssetClassListComponent } from './forms/assets/asset-classes/asset-class.list';
import { AssetTypeListComponent } from './forms/assets/asset-types/asset-type.list';
import { DivisionListComponent } from './forms/administrations/divisions/division.list';
import { PartnerListComponent } from './forms/documents/partners/partner.list';
import { CostCenterListComponent } from './forms/administrations/cost-centers/cost-center.list';
import { DimensionListComponent } from './forms/assets/dimensions/dimension.list';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { DictionaryItemListComponent } from './forms/administrations/dictionary-item/dictionary-item.list';
import { DictionaryItemDetailComponent } from './forms/administrations/dictionary-item/dictionary-item.detail';
import { FieldByColumnDefinitionPipe } from './forms/common/pipes/field-by-column-definition';
import { AssetAllListComponent } from './forms/assets/assets/asset-all.list';
import { BoolPipe } from './forms/common/pipes/bool.pipe';
import { BoolPipeState } from './forms/common/pipes/bool.pipe_state';
import { BoolPipeInventory } from './forms/common/pipes/bool-pipe.inventory';
import { MergePipe } from './forms/common/pipes/merge.pipe';
import { MergeRoom } from './forms/common/pipes/merge-room.pipe';
import { HighlightPipe } from './forms/common/pipes/highlight-pipe ';
import { ConfigValuesList } from './forms/common/config-values.list';
import { ConfigValuesDetail } from './forms/common/config-values.detail';
import { ConfigValuesManage } from './forms/common/config-values.manage';
import { InventoryManage } from './forms/inventory/inventory.manage';
import { InventoryList } from './forms/inventory/inventory.list';
import { InventoryDetail } from './forms/inventory/inventory.detail';
import { AccMonthListComponent } from './forms/accounting/acc-month.list';
import { AccMonthDetailComponent } from './forms/accounting/acc-month.detail';
import { AccMonthManageComponent } from './forms/accounting/acc-month.manage';
import { InvStateList } from './forms/inventory/inv-state/inv-state.list';
import { InvStateDetail } from './forms/inventory/inv-state/inv-state.detail';
import { InvStateManage } from './forms/inventory/inv-state/inv-state.manage';
import { AssetRecoAllListComponent } from './forms/assets/assets/asset-reco-all.list';
import { DictionaryItemManageComponent } from './forms/administrations/dictionary-item/dictionary-item.manage';
import { DictionaryTypeManageComponent } from './forms/administrations/dictionary-type/dictionary-type.manage';
import { DictionaryTypeDetailComponent } from './forms/administrations/dictionary-type/dictionary-type.detail';
import { DictionaryTypeListComponent } from './forms/administrations/dictionary-type/dictionary-type.list';
import { AdministrationManageComponent } from './forms/administrations/administrations/administration.manage';
import { AdministrationDetailComponent } from './forms/administrations/administrations/administration.detail';
import { AssetDetailUIComponent } from './forms/assets/assets/asset.detail.ui';
import { PartnerDetailComponent } from './forms/documents/partners/partner.detail';
import { PartnerManageComponent } from './forms/documents/partners/partner.manage';
import { RoomDetailComponent } from './forms/administrations/rooms/room.detail';
import { RoomManageComponent } from './forms/administrations/rooms/room.manage';
import { UomDetailComponent } from './forms/assets/uoms/uom.detail';
import { UomManageComponent } from './forms/assets/uoms/uom.manage';
import { CostCenterDetailComponent } from './forms/administrations/cost-centers/cost-center.detail';
import { CostCenterManageComponent } from './forms/administrations/cost-centers/cost-center.manage';
import { LocationDetailComponent } from './forms/administrations/locations/location.detail';
import { LocationManageComponent } from './forms/administrations/locations/location.manage';
import { EmployeeDetailComponent } from './forms/administrations/employees/employee.detail';
import { EmployeeManageComponent } from './forms/administrations/employees/employee.manage';
import { DepartmentDetailComponent } from './forms/administrations/departments/department.detail';
import { DepartmentManageComponent } from './forms/administrations/departments/department.manage';
import { AssetTypeDetailComponent } from './forms/assets/asset-types/asset-type.detail';
import { AssetTypeManageComponent } from './forms/assets/asset-types/asset-type.manage';
import { AssetCategoryDetailComponent } from './forms/assets/asset-categories/asset-category.detail';
import { AssetCategoryManageComponent } from './forms/assets/asset-categories/asset-category.manage';
import { EntityFileListComponent } from './forms/common/entity-file.list';
import { AssetOpDetailListComponent } from './forms/assets/asset-ops/asset-op.detail.list';
import { FieldByNamePipe } from './forms/common/pipes/field-by-name.pipe';
import { AssetInventoryReportComponent } from './forms/assets/assets/asset-inventory-report';
import { UserListComponent } from './forms/auth/user.list';
import { AssetInventoryManageComponent } from './forms/assets/assets/asset-inventory.manage';
import { AssetInvTempDetailListComponent } from './forms/assets/assets/asset-inv-temp-detail.list';
import { AssetInvFullDetailListComponent } from './forms/assets/assets/asset-inv-full-detail.list';
import { AssetListComponent } from './forms/assets/assets/asset.list';
import { AssetDepManageComponent } from './forms/assets/assets/asset-dep.manage';
import { AdministrationHttpService } from './services/http/administration/administration.http.service';
import { AssetHttpService } from './services/http/assets/asset.http.service';
import { UomHttpService } from './services/http/assets/uom.http.service';
import { DivisionHttpService } from './services/http/administration/division.http.service';
import { AdmCenterDetailComponent } from './forms/administrations/adm-centers/adm-center.detail';
import { AdmCenterManageComponent } from './forms/administrations/adm-centers/adm-center.manage';
import { AssetEntityListComponent } from './forms/assets/assets/asset-entity.list';
import { IdentityManage } from './forms/auth/identity.manage';
import { RoleList } from './forms/auth/role.list';
import { RoleService } from './services/http/identity/role.service';
import { CompanyDetailComponent } from './forms/assets/companies/company.detail';
import { CompanyManageComponent } from './forms/assets/companies/company.manage';
import { AuthorizationService } from './services/authorization.service';
import { AssetStateManageComponent } from './forms/assets/asset-states/asset-state.manage';
import { AssetStateListComponent } from './forms/assets/asset-states/asset-state.list';
import { AssetStateDetailComponent } from './forms/assets/asset-states/asset-state.detail';
import { MasterTypeManageComponent } from './forms/assets/master-types/master-type.manage';
import { MasterTypeListComponent } from './forms/assets/master-types/master-type.list';
import { MasterTypeDetailComponent } from './forms/assets/master-types/master-type.detail';
import { LocationHttpService } from './services/http/administration/location.http.service';
import { BudgetValidateList } from './forms/administrations/budget/budget-validate.list';
import { BudgetValidateManage } from './forms/administrations/budget/budget-validate.manage';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BudgetDetailUI } from './forms/administrations/budget/budget.detail.ui';
import { AccountList } from './forms/administrations/account/account.list';
import { ProjectManage } from './forms/assets/projects/project.manage';
import { ProjectList } from './forms/assets/projects/project.list';
import { ProjectDetail } from './forms/assets/projects/project.detail';
import { SubTypeList } from './forms/administrations/sub-types/sub-type.list';
import { SubTypeManage } from './forms/administrations/sub-types/sub-type.manage';
import { SubTypeDetail } from './forms/administrations/sub-types/sub-type.detail';
import { TypeManage } from './forms/administrations/types/type.manage';
import { TypeDetail } from './forms/administrations/types/type.detail';
import { TypeList } from './forms/administrations/types/type.list';
import { EntityFileUploadComponent } from './forms/common/entity-file-upload';
import { EntityFileDownloadComponent } from './forms/common/entity-file-download';
import { BudgetOpDetailList } from './forms/administrations/budget-ops/budget-op.detail.list';
import { BudgetOpManage } from './forms/administrations/budget-ops/budget-op-manage';
import { BudgetManage } from './forms/administrations/budget/budget.manage';
import { BudgetList } from './forms/administrations/budget/budget.list';
import { BudgetHttpService } from './services/http/administration/budget.http.service';
import { OrderValidateList } from './forms/administrations/order/order-validate.list';
import { OfferValidateList } from './forms/administrations/offer/offer-validate.list';
import { OfferValidateManage } from './forms/administrations/offer/offer-validate.manage';
import { OfferDetailUI } from './forms/administrations/offer/offer.detail.ui';
import { OfferOpDetailList } from './forms/administrations/offer-ops/offer-op.detail.list';
import { SanitizeHtml } from './forms/common/pipes/sanitize.html-pipe';
import { OfferManage } from './forms/administrations/offer/offer.manage';
import { OfferList } from './forms/administrations/offer/offer.list';
import { OfferHttpService } from './services/http/administration/offer.http.service';
import { OfferOpManage } from './forms/administrations/offer-ops/offer-op-manage';
import { OrderValidateManage } from './forms/administrations/order/order-validate.manage';
import { OrderDetailUI } from './forms/administrations/order/order.detail.ui';
import { OrderOpDetailList } from './forms/administrations/order-ops/order-op.detail.list';
import { OrderList } from './forms/administrations/order/order.list';
import { OrderManage } from './forms/administrations/order/order.manage';
import { OrderOpManage } from './forms/administrations/order-ops/order-op-manage';
import { OrderHttpService } from './services/http/administration/order.http.service';
import { EmailManagerManage } from './forms/administrations/email-manager/email-manager.manage';
import { EmailManagerList } from './forms/administrations/email-manager/email-manager.list';
import { EmailManagerDetail } from './forms/administrations/email-manager/email-manager.detail';
import { EmailTypeDetail } from './forms/administrations/email-type/email-type.detail';
import { EmailTypeList } from './forms/administrations/email-type/email-type.list';
import { EmailTypeManage } from './forms/administrations/email-type/email-type.manage';
import { EmailManagerHttpService } from './services/http/administration/email-manager.http.service';
import { EmailTypeHttpService } from './services/http/administration/email-type.http.service';
import { AppStateHttpService } from './services/http/common/app-state.http.service';
import { EmailManagerDetailHttpService } from './services/http/administration/email-manager-detail.http.service';
import { AssetComponentManage } from './forms/assets/asset-components/asset-component.manage';
import { AssetComponentList } from './forms/assets/asset-components/asset-component.list';
import { AssetComponentDetail } from './forms/assets/asset-components/asset-component.detail';
import { AssetComponentDetailHttpService } from './services/http/assets/asset-component-detail.http.service';
import { AssetComponentOpDetailList } from './forms/assets/asset-component-ops/asset-component-op.detail.list';
import { EmployeeHttpService } from './services/http/administration/employee.http.service';
import { SubTypeHttpService } from './services/http/administration/sub-type.http.service';
import { AssetComponentHttpService } from './services/http/assets/asset-component.http.service';
import { EntityFileHttpService } from './services/http/common/entity-file.http.service';
import { EmployeeDetailUIComponent } from './forms/administrations/employees/employee.detail.ui';
import { DepartmentHttpService } from './services/http/administration/department.http.service';
import { EmployeeDetailHttpService } from './services/http/administration/employee-detail.http.service';
import { AssetEmployeeValidateList } from './forms/assets/assets/asset-employee-validate.list';
import { AssetInventoryEmployeeValidateManage } from './forms/assets/assets/asset-inventory-employee-validate.manage';
import { AssetInventoryEmailManage } from './forms/assets/assets/asset-inventory-email.manage';
import { AssetInvEmailList } from './forms/assets/assets/asset-inv-email.list';
import { AssetTypeHttpService } from './services/http/assets/asset-type.http.service';
import { AssetCategoryHttpService } from './services/http/assets/asset-category.http.service';
import { AdmCenterHttpService } from './services/http/administration/adm-center.http.service';
import { InventoryHttpService } from './services/http/inventory/inventory.http.service';
import { RegionHttpService } from './services/http/administration/region.http.service';
import { RoomDetailHttpService } from './services/http/administration/room-detail.http.service';
import { CompanyHttpService } from './services/http/assets/company.http.service';
import { CityHttpService } from './services/http/administration/city.http.service';
import { InvStateHttpService } from './services/http/inventory/inv-state.http.service';
import { DocumentTypeHttpService } from './services/http/documents/document-type.http.service';
import { AssetInvDetailListComponent } from './forms/assets/assets/asset-inv-detail.list';
import { AssetDepDetailListComponent } from './forms/assets/assets/asset-dep-detail.list';
import { AssetClassManageComponent } from './forms/assets/asset-classes/asset-class.manage';
import { AssetClassDetailComponent } from './forms/assets/asset-classes/asset-class.detail';
import { DocumentHttpService } from './services/http/documents/document.http.service';
import { AssetOpManageComponent } from './forms/assets/asset-ops/asset-op-manage';
import { AccountHttpService } from './services/http/administration/account.http.service';
import { TypeHttpService } from './services/http/administration/type.http.service';
import { MasterTypeHttpService } from './services/http/assets/master-type.http.service';
import { ProjectHttpService } from './services/http/assets/project.http.service';
import { InterCompanyHttpService } from './services/http/assets/inter-company.http.service';
import { BudgetOpHttpService } from './services/http/administration/budget-op.http.service';
import { AssetOpHttpService } from './services/http/assets/asset-op.http.service';
import { DimensionHttpService } from './services/http/administration/dimension.http.service';
import { DocumentTypeDropDownListComponent } from './forms/documents/document-types/document-type.drop-down.list';
import { DimensionManageComponent } from './forms/assets/dimensions/dimension.manage';
import { DimensionDetailComponent } from './forms/assets/dimensions/dimension.detail';
import { PaginationsComponent } from './views/base/paginations.component';
import { JwtHelperService, JwtModuleOptions, JWT_OPTIONS } from '@auth0/angular-jwt';
import { AccMonthHttpService } from './services/http/accounting/acc-month.http.service';
import { CostCenterHttpService } from './services/http/administration/cost-center.http.service';
import { BaThemeConfigProvider } from './services/http/common/theme-colors.service';
import { RouteManageComponent } from './forms/common/route-definition/route.manage';
import { RouteListComponent } from './forms/common/route-definition/route.list';
import { RouteDetailComponent } from './forms/common/route-definition/route.detail';
import { RouteHttpService } from './services/http/common/route.http.service';
import { RouteChildManageComponent } from './forms/common/route-child-definition/route-child.manage';
import { RouteChildHttpService } from './services/http/common/route-child.http.service';
import { HideIfNotUnauthorizedDirective } from './directives/hideIfNotAuthorized';
import { DisableIfNotUnauthorizedDirective } from './directives/disableIfNotAuthorized';
import { PermissionTypeHttpService } from './services/http/common/permission-type.http.service';
import { PermissionHttpService } from './services/http/common/permission.http.service';
import { PermissionRoleHttpService } from './services/http/common/permission-role.http.service';
import { DashboardHttpService } from './services/http/common/dashboard.http.service';
import { OperationDetail } from './forms/documents/operations/operation.detail';
import { AssetMemoryService } from './services/memory/asset.memory.service';
import { AssetComponentOpHttpService } from './services/http/assets/asset-component-op.http.service';
import { DashboardOfferComponent } from './views/dashboard/dashboard-offer.component';
import { DashboardOrderComponent } from './views/dashboard/dashboard-order.component';
import { DashboardInventoryComponent } from './views/dashboard/dashboard-inventory.component';
import { AssetNatureHttpService } from './services/http/assets/asset-nature.http.service';
import { AssetNatureManageComponent } from './forms/assets/asset-natures/asset-nature.manage';
import { AssetNatureListComponent } from './forms/assets/asset-natures/asset-nature.list';
import { AssetNatureDetailComponent } from './forms/assets/asset-natures/asset-nature.detail';
import { InsuranceCategoryDetail } from './forms/assets/insurance-categories/insurance-category.detail';
import { InsuranceCategoryList } from './forms/assets/insurance-categories/insurance-category.list';
import { InsuranceCategoryManage } from './forms/assets/insurance-categories/insurance-category.manage';
import { InsuranceCategoryHttpService } from './services/http/assets/insurance-category.http.service';
import { TableDefinitionManageComponent } from './forms/common/table-definition/table-definition.manage';
import { ColumnDefinitionListComponent } from './forms/common/column-definition/column-definition.list';
import { ColumnDefinitionDetail } from './forms/common/column-definition/column-definition.detail';
import { RouteChildListComponent } from './forms/common/route-child-definition/route-child.list';
import { RouteChildDetailComponent } from './forms/common/route-child-definition/route-child.detail';
import { PermissionTypeManage } from './forms/common/permission-type/permission-type.manage';
import { PermissionTypeList } from './forms/common/permission-type/permission-type.list';
import { PermissionTypeDetail } from './forms/common/permission-type/permission-type.detail';
import { PermissionManage } from './forms/common/permission/permission.manage';
import { PermissionList } from './forms/common/permission/permission.list';
import { PermissionDetail } from './forms/common/permission/permission.detail';
import { PermissionRoleManage } from './forms/common/permission-role/permission-role.manage';
import { PermissionRoleList } from './forms/common/permission-role/permission-role.list';
import { PermissionRoleDetail } from './forms/common/permission-role/permission-role.detail';
import { ColumnDefinitionManageComponent } from './forms/common/column-definition/column-definition.manage';
import { TableDefinitionDetailComponent } from './forms/common/table-definition/table-definition.detail';
import { TableDefinitionListComponent } from './forms/common/table-definition/table-definition.list';
import { ToastrModule } from 'ngx-toastr';
import { NotificationService } from './services/notification.service';
import { RegisterComponent } from './views/register/register.component';
import { BrandDetail } from './forms/assets/brands/brand.detail';
import { BrandList } from './forms/assets/brands/brand.list';
import { BrandManage } from './forms/assets/brands/brand.manage';
import { BrandHttpService } from './services/http/assets/brand.http.service';
import { BadgeManage } from './forms/common/badge/badge.manage';
import { BadgeDetail } from './forms/common/badge/badge.detail';
import { BadgeList } from './forms/common/badge/badge.list';
import { BadgeHttpService } from './services/http/common/badge.http.service';
import { AccountManage } from './forms/administrations/account/account.manage';
import { AccountDetail } from './forms/administrations/account/account.detail';
import { ExpAccountDetail } from './forms/administrations/exp-account/exp-account.detail';
import { ExpAccountList } from './forms/administrations/exp-account/exp-account.list';
import { ExpAccountManage } from './forms/administrations/exp-account/exp-account.manage';
import { ExpAccountHttpService } from './services/http/administration/exp-account.http.service';
import { ArticleDetail } from './forms/administrations/article/article.detail';
import { ArticleList } from './forms/administrations/article/article.list';
import { ArticleManage } from './forms/administrations/article/article.manage';
import { ArticleHttpService } from './services/http/administration/article.http.service';
import { DivisionManageComponent } from './forms/administrations/divisions/division.manage';
import { DivisionDetailComponent } from './forms/administrations/divisions/division.detail';
import { BudgetManagerList } from './forms/administrations/budget-manager/budget-manager.list';
import { BudgetManagerDetail } from './forms/administrations/budget-manager/budget-manager.detail';
import { BudgetManagerHttpService } from './services/http/administration/budget-manager.http.service';
import { BudgetManagerManage } from './forms/administrations/budget-manager/budget-manager.manage';
import { MaterialManage } from './forms/administrations/materials/material.manage';
import { MaterialList } from './forms/administrations/materials/material.list';
import { MaterialDetail } from './forms/administrations/materials/material.detail';
import { MaterialHttpService } from './services/http/administration/material.http.service';
import { RegionManageComponent } from './forms/administrations/regions/region.manage';
import { RegionDetailComponent } from './forms/administrations/regions/region.detail';
import { EmployeeCostCenterManage } from './forms/administrations/employee-cost-centers/employee-cost-center.manage';
import { EmployeeCostCenterList } from './forms/administrations/employee-cost-centers/employee-cost-center.list';
import { EmployeeCostCenterDetail } from './forms/administrations/employee-cost-centers/employee-cost-center.detail';
import { EmployeeCostCenterHttpService } from './services/http/administration/employee-cost-center.http.service';
import { DashboardEntityListComponent } from './views/dashboard/dashboard-entity.list';
import { ActiveRowPipe } from './forms/common/pipes/active-row.pipe';
import { DashboardInventoryMarkComponent } from './views/dashboard/dashboard-inventory-mark.component';
import { DashboardListComponent } from './views/dashboard/dashboard.list';
import { NgSelectModule } from '@ng-select/ng-select';
import { RouteReuseStrategy } from '@angular/router';
import { CustomReuseStrategy } from './common/reuse-strategy';
import { IdentityService } from './services/http/identity/identity.service';
import { DeviceHttpService } from './services/http/common/device.http.service';
import { DeviceTypeHttpService } from './services/http/common/device-type.http.service';
import { DeviceDetailHttpService } from './services/http/common/device-detail.http.service';
import { DeviceDetail } from './forms/common/devices/device.detail';
import { DeviceList } from './forms/common/devices/device.list';
import { DeviceManage } from './forms/common/devices/device.manage';
import { DeviceTypeDetail } from './forms/common/device-types/device-type.detail';
import { DeviceTypeList } from './forms/common/device-types/device-type.list';
import { DeviceTypeManage } from './forms/common/device-types/device-type.manage';
import { AngularMaterialModule } from './angular-material.module';
import {TableModule} from 'primeng/table';
import {ToastModule} from 'primeng/toast';
import {CalendarModule} from 'primeng/calendar';
import {SliderModule} from 'primeng/slider';
import {MultiSelectModule} from 'primeng/multiselect';
import {ContextMenuModule} from 'primeng/contextmenu';
import {DialogModule} from 'primeng/dialog';
import {ButtonModule} from 'primeng/button';
import {DropdownModule} from 'primeng/dropdown';
import {ProgressBarModule} from 'primeng/progressbar';
import {InputTextModule} from 'primeng/inputtext';
import {CheckboxModule} from 'primeng/checkbox';
import {ToggleButtonModule} from 'primeng/togglebutton';
import {SplitButtonModule} from 'primeng/splitbutton';
import { MessageService } from 'primeng/api';
import { NgxEchartsModule } from 'ngx-echarts';
import { DashboardBudgetComponent } from './views/dashboard/Budget/dashboard-budget.component';
import { ActivityManage } from './forms/assets/activities/activity.manage';
import { ActivityList } from './forms/assets/activities/activity.list';
import { ActivityDetail } from './forms/assets/activities/activity.detail';
import { ActivityHttpService } from './services/http/assets/activity.http.service';
import { DashboardBudgetAssetComponent } from './views/dashboard/Budget/dashboard-budget-asset.component';
import {AngularTreeGridModule} from 'angular-tree-grid';
import {OverlayPanelModule} from 'primeng/overlaypanel';
import { BudgetPrimeNgList } from './forms/administrations/budget/budget-primeng.list';
import { EmailManagerUiList } from './forms/administrations/email-manager/email-manager-ui.list';
import { CategoryDetailComponent } from './forms/assets/categories/category.detail';
import { CategoryListComponent } from './forms/assets/categories/category.list';
import { CategoryManageComponent } from './forms/assets/categories/category.manage';
import { CategoryHttpService } from './services/http/assets/category.http.service';
import { InterCompanyENHttpService } from './services/http/assets/inter-company-en.http.service';
import { InterCompanyENManageComponent } from './forms/assets/inter-companies-en/inter-company-en.manage';
import { InterCompanyENListComponent } from './forms/assets/inter-companies-en/inter-company-en.list';
import { InterCompanyENDetailComponent } from './forms/assets/inter-companies-en/inter-company-en.detail';
import { InterCompanyManageComponent } from './forms/assets/inter-companies/inter-company.manage';
import { InterCompanyDetailComponent } from './forms/assets/inter-companies/inter-company.detail';
import { InterCompanyListComponent } from './forms/assets/inter-companies/inter-company.list';
import { CategoryENDetailComponent } from './forms/assets/categories-en/category-en.detail';
import { CategoryENListComponent } from './forms/assets/categories-en/category-en.list';
import { CategoryENManageComponent } from './forms/assets/categories-en/category-en.manage';
import { CategoryENHttpService } from './services/http/assets/category-en.http.service';
import { SubCategoryENDetailComponent } from './forms/assets/sub-categories-en/sub-category-en.detail';
import { SubCategoryENListComponent } from './forms/assets/sub-categories-en/sub-category-en.list';
import { SubCategoryENManageComponent } from './forms/assets/sub-categories-en/sub-category-en.manage';
import { SubCategoryENHttpService } from './services/http/assets/sub-category-en.http.service';
import { SubCategoryHttpService } from './services/http/assets/sub-category.http.service';
import { SubCategoryDetailComponent } from './forms/assets/sub-categories/sub-category.detail';
import { SubCategoryListComponent } from './forms/assets/sub-categories/sub-category.list';
import { SubCategoryManageComponent } from './forms/assets/sub-categories/sub-category.manage';
import { ModelDetail } from './forms/assets/models/model.detail';
import { ModelList } from './forms/assets/models/model.list';
import { ModelManage } from './forms/assets/models/model.manage';
import { ModelHttpService } from './services/http/assets/model.http.service';
import { EmployeeRoomHttpService } from './services/http/administration/employee-room.http.service';
import { EmployeeRoomDetail } from './forms/administrations/employee-rooms/employee-room.detail';
import { EmployeeRoomList } from './forms/administrations/employee-rooms/employee-room.list';
import { EmployeeRoomManage } from './forms/administrations/employee-rooms/employee-room.manage';
import { RoomHttpService } from './services/http/administration/room.http.service';
import { AssetEmployeeAddNewAssetListComponent } from './forms/assets/assets/asset-employee-add-new-asset.list';
import { AssetEmployeeDetailUI } from './forms/assets/assets/asset-employee.detail.ui';
import { DictionaryItemHttpService } from './services/http/administration/dictionary-item.http.service';
import { AssetOpRecoManageComponent } from './forms/assets/asset--reco-ops/asset-reco-op-manage';
import { AssetOpRecoDetailListComponent } from './forms/assets/asset--reco-ops/asset-reco-op.detail.list';

export function getToken() {
  const token = localStorage.getItem('id_token');
  alert(token);
  return token;
}

const JWT_Module_Options: JwtModuleOptions = {
  config: {
      tokenGetter: getToken,
      // whitelistedDomains: '1'
  }
};

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AngularMaterialModule,
    AppRoutingModule,
    AppAsideModule,
    AppBreadcrumbModule.forRoot(),
    AppFooterModule,
    AppHeaderModule,
    AppSidebarModule,
    CommonModule,
    FormsModule,
    NgSelectModule,
    ModalModule.forRoot(),
    ReactiveFormsModule,
    ToastrModule.forRoot(),
    CalendarModule,
		SliderModule,
		DialogModule,
		MultiSelectModule,
		ContextMenuModule,
		DropdownModule,
		ButtonModule,
		ToastModule,
    InputTextModule,
    CheckboxModule,
    ToggleButtonModule,
    OverlayPanelModule,
    SplitButtonModule,
    ProgressBarModule,
    HttpClientModule,
    AngularTreeGridModule,
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts'),
    }),
    // JwtModule.forRoot({
    //   config: {
    //     tokenGetter: getToken,
    //     allowedDomains: ['localhost:7000', 'localhost:4200'],
    //     // disallowedRoutes: ['http://example.com/examplebadroute/'],
    //     // authScheme: 'Basic',
    //     headerName: 'Authorization',
    //   },
    // }),
    
    PerfectScrollbarModule,
    BsDropdownModule.forRoot(),
    TabsModule.forRoot(),
    ChartsModule,
    PaginationModule.forRoot(),
    ProgressbarModule.forRoot(),
    CollapseModule.forRoot(),

  ],
  declarations: [
    AppComponent,
    ...APP_CONTAINERS,
    P404Component,
    P500Component,
    LoginComponent,
    RegisterComponent,
    AssetListComponent,
    AssetDepManageComponent,
    AssetManageComponent,
    AssetRecoManageComponent,
    AssetAllListComponent,
    AssetRecoListComponent,
    AssetRecoAllListComponent,
    AssetDetailUIComponent,
    DepartmentListComponent,
    DepartmentDetailComponent,
    DepartmentManageComponent,
    EmployeeListComponent,
    EmployeeDetailComponent,
    EmployeeManageComponent,
    EmployeeDetailUIComponent,
    RegionListComponent,
    AdmCenterListComponent,
    AdmCenterDetailComponent,
    AdmCenterManageComponent,
    LocationListComponent,
    LocationDetailComponent,
    LocationManageComponent,
    RoomListComponent,
    RoomDetailComponent,
    RoomManageComponent,
    AdministrationListComponent,
    AssetCategoryListComponent,
    AssetCategoryDetailComponent,
    AssetCategoryManageComponent,
    UomListComponent,
    UomDetailComponent,
    UomManageComponent,
    CompanyListComponent,
    CompanyDetailComponent,
    CompanyManageComponent,
    AssetClassListComponent,
    AssetTypeListComponent,
    AssetTypeDetailComponent,
    AssetTypeManageComponent,
    DivisionListComponent,
    PartnerListComponent,
    PartnerDetailComponent,
    PartnerManageComponent,
    CostCenterListComponent,
    CostCenterDetailComponent,
    CostCenterManageComponent,
    DimensionListComponent,
    DictionaryItemListComponent,
    DictionaryItemDetailComponent,
    CountryListComponent,
    CountryManageComponent,
    CountryDetailComponent,
    CountyManageComponent,
    CountyListComponent,
    CountyDetailComponent,
    CityListComponent,
    CityManageComponent,
    CityDetailComponent,
    FieldByNamePipe,
    FieldByColumnDefinitionPipe,
    ConfigValuesList,
    ConfigValuesDetail,
    ConfigValuesManage,
    TableDefinitionManageComponent,
    TableDefinitionListComponent,
    ColumnDefinitionListComponent,
    ColumnDefinitionDetail,
    InventoryManage,
    InventoryList,
    InventoryDetail,
    AccMonthListComponent,
    AccMonthDetailComponent,
    AccMonthManageComponent,
    InvStateList,
    InvStateDetail,
    InvStateManage,
    DictionaryItemManageComponent,
    DictionaryItemDetailComponent,
    DictionaryItemListComponent,
    DictionaryTypeManageComponent,
    DictionaryTypeDetailComponent,
    DictionaryTypeListComponent,
    AdministrationListComponent,
    AdministrationManageComponent,
    AdministrationDetailComponent,
    EntityFileListComponent,
    AssetOpDetailListComponent,
    AssetInventoryReportComponent,
    AssetInventoryManageComponent,
    AssetInvTempDetailListComponent,
    AssetInvFullDetailListComponent,
    UserListComponent,
    AssetEntityListComponent,
    IdentityManage,
    RoleList,
    AssetStateManageComponent,
    AssetStateListComponent,
    AssetStateDetailComponent,
    MasterTypeManageComponent,
    MasterTypeListComponent,
    MasterTypeDetailComponent,
    BudgetValidateList,
    BudgetValidateManage,
    BudgetDetailUI,
    AccountList,
    InterCompanyManageComponent,
    InterCompanyDetailComponent,
    InterCompanyListComponent,
    InsuranceCategoryDetail,
    InsuranceCategoryList,
    InsuranceCategoryManage,
    ProjectManage,
    ProjectList,
    ProjectDetail,
    SubTypeList,
    SubTypeManage,
    SubTypeDetail,
    TypeManage,
    TypeDetail,
    TypeList,
    EntityFileUploadComponent,
    EntityFileDownloadComponent,
    BudgetOpDetailList,
    BudgetOpManage,
    BudgetManage,
    BudgetList,
    OfferValidateList,
    OfferValidateManage,
    OfferDetailUI,
    OfferOpDetailList,
    SanitizeHtml,
    OfferManage,
    OfferList,
    OfferOpManage,
    OrderValidateList,
    OrderValidateManage,
    OrderDetailUI,
    OrderOpDetailList,
    OrderList,
    OrderManage,
    OrderOpDetailList,
    OrderOpManage,
    EmailManagerManage,
    EmailManagerList,
    EmailManagerDetail,
    EmailTypeDetail,
    EmailTypeList,
    EmailTypeManage,
    AssetComponentDetail,
    AssetComponentManage,
    AssetComponentList,
    AssetComponentOpDetailList,
    AssetEmployeeValidateList,
    AssetInventoryEmployeeValidateManage,
    AssetInventoryEmailManage,
    AssetInvEmailList,
    AssetInvDetailListComponent,
    AssetDepDetailListComponent,
    AssetClassManageComponent,
    AssetClassDetailComponent,
    AssetOpManageComponent,
    DocumentTypeDropDownListComponent,
    DimensionManageComponent,
    DimensionDetailComponent,
    DimensionDetailComponent,
    PaginationsComponent,
    DashboardBudgetComponent,
    RouteManageComponent,
    RouteListComponent,
    RouteDetailComponent,
    RouteChildManageComponent,
    RouteChildListComponent,
    RouteChildDetailComponent,
    HighlightPipe,
    HideIfNotUnauthorizedDirective, 
    DisableIfNotUnauthorizedDirective,
    PermissionTypeManage,
    PermissionTypeList,
    PermissionTypeDetail,
    PermissionManage,
    PermissionList,
    PermissionDetail,
    PermissionRoleManage,
    PermissionRoleList,
    PermissionRoleDetail,
    DocumentTypeDropDownListComponent,
    OperationDetail,
    DashboardOfferComponent,
    DashboardOrderComponent,
    DashboardInventoryComponent,
    DashboardInventoryMarkComponent,
    AssetNatureManageComponent,
    AssetNatureListComponent,
    AssetNatureDetailComponent,
    ColumnDefinitionManageComponent,
    TableDefinitionDetailComponent,
    BrandDetail,
    BrandList,
    BrandManage,
    BadgeManage,
    BadgeDetail,
    BadgeList,
    AccountManage,
    AccountList,
    AccountDetail,
    ExpAccountManage,
    ExpAccountList,
    ExpAccountDetail,
    ArticleManage,
    ArticleList,
    ArticleDetail,
    DivisionManageComponent,
    DivisionListComponent,
    DivisionDetailComponent,
    BudgetManagerManage,
    BudgetManagerList,
    BudgetManagerDetail,
    MaterialManage,
    MaterialList,
    MaterialDetail,
    RegionManageComponent,
    RegionListComponent,
    RegionDetailComponent,
    EmployeeCostCenterManage,
    EmployeeCostCenterList,
    EmployeeCostCenterDetail,
    DashboardEntityListComponent,
    DashboardListComponent,
    DeviceDetail,
    DeviceList,
    DeviceManage,
    DeviceTypeDetail,
    DeviceTypeList,
    DeviceTypeManage,
    ActivityManage,
    ActivityList,
    ActivityDetail,
    DashboardBudgetAssetComponent,
    BudgetList,
    BudgetPrimeNgList,
    EmailManagerUiList,
    CategoryDetailComponent,
    CategoryListComponent,
    CategoryManageComponent,
    InterCompanyENManageComponent,
    InterCompanyENListComponent,
    InterCompanyENDetailComponent,
    CategoryENDetailComponent,
    CategoryENListComponent,
    CategoryENManageComponent,
    SubCategoryENDetailComponent,
    SubCategoryENListComponent,
    SubCategoryENManageComponent,
    SubCategoryDetailComponent,
    SubCategoryListComponent,
    SubCategoryManageComponent,
    ModelDetail,
    ModelList,
    ModelManage,
    EmployeeRoomDetail,
    EmployeeRoomList,
    EmployeeRoomManage,
    AssetEmployeeAddNewAssetListComponent,
    AssetEmployeeDetailUI,
    AssetOpRecoManageComponent,
    AssetOpRecoDetailListComponent

  ],
  providers: [{
    provide: LocationStrategy,
    useClass: HashLocationStrategy,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: TokenInterceptor,
    multi: true
  },
  { provide: JWT_OPTIONS, useValue: JWT_OPTIONS },
  { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
  JwtHelperService,
  AuthGuard,
  AuthorizationService,
  ActivityHttpService,
  AccMonthHttpService,
  BaThemeConfigProvider,
  AppStateHttpService,
  AccountHttpService,
  SubTypeHttpService,
  TypeHttpService,
  MasterTypeHttpService,
  BrandHttpService,
  ProjectHttpService,
  InterCompanyHttpService,
  BudgetOpHttpService,
  CostCenterHttpService,
  AssetComponentOpHttpService,
  DictionaryItemHttpService,
  AssetMemoryService,
  AssetHttpService,
  AssetOpHttpService,
  AssetTypeHttpService,
  AssetCategoryHttpService,
  AdmCenterHttpService,
  InventoryHttpService,
  CityHttpService,
  DimensionHttpService,
  RegionHttpService,
  RoomDetailHttpService,
  CompanyHttpService,
  InvStateHttpService,
  InsuranceCategoryHttpService,
  DocumentTypeHttpService,
  AssetComponentDetailHttpService,
  AdministrationHttpService,
  AssetNatureHttpService,
  NotificationService,
  BudgetHttpService,
  OfferHttpService,
  OrderHttpService,
  DocumentHttpService,
  DatePipe,
  DecimalPipe,
  BoolPipe,
  BoolPipeState,
  BoolPipeInventory,
  MergePipe,
  MergeRoom,
  HighlightPipe,
  ActiveRowPipe,
  AuthenticationService,
  BsModalService,
  BsModalRef,
  ConfigValuesHttpService,
  ColumnDefinitionHttpService,
  CountyHttpService,
  CountryHttpService,
  UomHttpService,
  DivisionHttpService,
  DepartmentHttpService,
  EmployeeDetailHttpService,
  LocationHttpService,
  EmailManagerHttpService,
  EmailManagerDetailHttpService,
  EmailTypeHttpService,
  EmployeeHttpService,
  SubTypeHttpService,
  EntityFileHttpService,
  AssetComponentHttpService,
  PermissionTypeHttpService,
  PermissionHttpService,
  PermissionRoleHttpService,
  RoleService,
  RouteHttpService,
  RouteChildHttpService,
  DashboardHttpService,
  IdentityService,
  BadgeHttpService,
  ExpAccountHttpService,
  ArticleHttpService,
  BudgetManagerHttpService,
  MaterialHttpService,
  EmployeeCostCenterHttpService,
  EmployeeRoomHttpService,
  DeviceHttpService,
  DeviceTypeHttpService,
  DeviceDetailHttpService,
  CategoryHttpService,
  CategoryENHttpService,
  InterCompanyENHttpService,
  SubCategoryENHttpService,
  SubCategoryHttpService,
  RoomHttpService,
  ModelHttpService,
  MessageService,
  TableDefinitionHttpService],
  exports: [PaginationComponent, HighlightPipe, HideIfNotUnauthorizedDirective, DisableIfNotUnauthorizedDirective],
  bootstrap: [ AppComponent ]
})

export class AppModule { }
